import { action, computed, makeObservable, observable, toJS } from "mobx";

import { OnlyOneFilter, OrFilter, StringFilter } from "./GridFilters";
import { gridTypesEnum } from "../utils/SchoolBlocksUtilities";
import PersonBlockObject from "../components/blocks/SchoolBlocks/BlockObjects/PersonBlockObject";
import { blockObjectFactory } from "../components/blocks/SchoolBlocks/blockObjectFactory";
import BlockObject from "../components/blocks/SchoolBlocks/BlockObjects/BlockObject";
import { ReactiveContentItem } from "../components/schoolfeed/SchoolFeedContentItem";
import { ReactiveAccordionItem } from "../components/schoolfeed/ReactiveAccordionItem";
import { IView } from "./InterfaceStore";

class BlockVisibility {
  id: string | number;
  allBoolFilters = new Map<string, boolean>();
  allMapFilters = new Map<string, Map<string | number, boolean>>();

  constructor(id: string | number) {
    makeObservable(this, {
      setMapFilterValues: action,
      setBoolFilterValues: action,
      resetFilterValues: action,
      isVisible: computed,
      allBoolFilters: observable,
      allMapFilters: observable,
    });
    this.id = id;
  }

  setMapFilterValues(filterName: string, id: string | number, value: boolean) {
    if (!this.allMapFilters.has(filterName)) {
      this.allMapFilters.set(filterName, observable.map({}));
    }

    const filter = this.allMapFilters.get(filterName)!;
    filter.set(id, value);
  }

  setBoolFilterValues(filterName: string, value: boolean) {
    this.allBoolFilters.set(filterName, value);
  }

  resetFilterValues(filterName: string) {
    if (this.allMapFilters.has(filterName)) {
      const filter = this.allMapFilters.get(filterName)!;
      filter.clear();
    }

    if (this.allBoolFilters.has(filterName)) {
      this.allBoolFilters.set(filterName, true);
    }
  }

  get isVisible() {
    let visibleByFilters: any = true;

    this.allBoolFilters.forEach((isVisibleByFilter) => {
      visibleByFilters &= isVisibleByFilter as any;
    });

    let start, visibleByThisFilter: any;
    this.allMapFilters.forEach((filter) => {
      start = true;
      visibleByThisFilter = true;
      filter.forEach((isVisibleByFilter) => {
        if (start) {
          start = false;
          visibleByThisFilter = isVisibleByFilter;
        } else {
          visibleByThisFilter |= isVisibleByFilter as any;
        }
      });
      visibleByFilters &= visibleByThisFilter;
    });

    return visibleByFilters;
  }
}

export type IGridStoreInitData = {
  view: IView;
  blocks?: any[];
};

export default class GridStore {
  blocks = new Map<string | number, any>();
  blockVisibility = new Map<string | number, BlockVisibility>();
  id: string | number | null = null;
  mapEnabled = false;
  mapVisible = false;
  canEdit = true;
  gridType: gridTypesEnum | null = null;
  initData: IGridStoreInitData | null = null;
  initialized = false;
  textFilter!: StringFilter;
  categoryFilters!: OrFilter;
  tagFilters!: OnlyOneFilter;
  blockTypeFilters!: OrFilter;

  constructor(initData: IGridStoreInitData | undefined) {
    this.initialize(initData);

    makeObservable(this, {
      blocks: observable,
      blockVisibility: observable,
      id: observable,
      mapEnabled: observable,
      mapVisible: observable,
      canEdit: observable,
      gridType: observable,
      setId: action,
      setBlocks: action,
      resizeBlock: action,
      relocateBlock: action,
      lockBlock: action,
      unlockBlock: action,
      deleteBlock: action,
      sortBlocksManually: action,
      setMapEnabled: action,
      toggleMapVisibility: action,
      getSortedBlocksByAlpha: action,
      loadedBlockIds: computed,
      visibleBlockIds: computed,
      totalVisible: computed,
      hasVisibleBlocks: computed,
      shouldShowMap: computed,
    });
    this.textFilter = new StringFilter(this);
    this.categoryFilters = new OrFilter(this, "filterCategories");
    this.tagFilters = new OnlyOneFilter(this, "filterTags");
    this.blockTypeFilters = new OrFilter(this, "filterBlockType");
  }

  initialize = (initData: IGridStoreInitData | undefined) => {
    if (initData) {
      this.initData = {
        view: initData.view,
      };

      if (initData.blocks) {
        this.setBlocks(initData.blocks, true, initData.view === IView.ALPHA);
      }

      this.initialized = true;
    }
  };

  serialize = () => {
    return {
      blocks: toJS(this.blocks),
      view: this.initData!.view,
    };
  };

  setId(id: string | number) {
    this.id = id;
  }

  setGridType(gridType: gridTypesEnum) {
    this.gridType = gridType;
  }

  getSortedBlocksByAlpha(blocks: Iterable<any>) {
    if (this.gridType === gridTypesEnum.ALPHA_GRID) {
      return [...blocks].sort((a, b) => {
        const orderingStringA =
          a.blockType === "person"
            ? a.blockModel.lname + a.blockModel.fname
            : a.filterString;
        const orderingStringB =
          b.blockType === "person"
            ? b.blockModel.lname + b.blockModel.fname
            : b.filterString;
        return orderingStringA && orderingStringB
          ? orderingStringA
              .toUpperCase()
              .localeCompare(orderingStringB.toUpperCase())
          : 1;
      });
    }
    return Array.from(blocks);
  }

  getBlockAsJs(blockId: string | number) {
    const block = this.blocks.get(blockId);
    if (block) {
      return toJS(block);
    }
    return null;
  }

  setBlocks(blocks: any[], clear = false, sort = false) {
    if (clear) {
      this.blocks.clear();
      this.blockVisibility.clear();
    }
    blocks.forEach((block) => {
      let blockCopy;
      if (
        block.hasOwnProperty("blockType") &&
        !(block instanceof BlockObject)
      ) {
        blockCopy = blockObjectFactory(block.type, block); // do not mutate blocks
        //@ts-ignore
        if (blockCopy.blockType === "person") {
          //@ts-ignore
          this.blocks.set(
            blockCopy.id,
            new PersonBlockObject(blockCopy.blockType, blockCopy),
          );
        } else {
          this.blocks.set(
            blockCopy.id,
            blockObjectFactory(blockCopy.blockType, blockCopy),
          );
        }
      } else {
        blockCopy = { ...block }; // do not mutate blocks
        // assume these are terraces
        if (blockCopy.content_items) {
          blockCopy.content_items = blockCopy.content_items.map((item) => {
            return new ReactiveContentItem(item);
          });
        }
        if (blockCopy.type === "accordion" && blockCopy.items) {
          blockCopy.items = blockCopy.items.map((item) => {
            return new ReactiveAccordionItem(item);
          });
        }
        this.blocks.set(blockCopy.id, blockCopy);
      }
      this.blockVisibility.set(blockCopy.id, new BlockVisibility(blockCopy.id));
    });
    if (sort) {
      // will sort all blocks in grid - should only be used when building a new grid or adding a new block
      this.sortBlocksManually(
        this.getSortedBlocksByAlpha(this.blocks.values()).map((b) => b.id),
      );
    }
  }

  resizeBlock(id: string | number, sizeX: number, sizeY: number) {
    const block = this.blocks.get(id);
    block.sizeX = sizeX;
    block.sizeY = sizeY;
  }

  relocateBlock(id: string | number, posX: number, posY: number) {
    const block = this.blocks.get(id);
    block.posX = posX;
    block.posY = posY;
  }

  lockBlock(id: string | number, posX: number, posY: number) {
    const block = this.blocks.get(id);
    block.positionLocked = true;
    this.relocateBlock(id, posX, posY);
  }

  unlockBlock(id: string | number) {
    const block = this.blocks.get(id);
    block.positionLocked = false;
  }

  deleteBlock(id: string | number) {
    this.blocks.delete(id);
    this.blockVisibility.delete(id);
  }

  sortBlocksManually(newOrder: Array<string | number>) {
    const newMap = new Map<string | number, any>();
    let block;
    newOrder.forEach((id) => {
      block = this.blocks.get(id);
      if (block) {
        newMap.set(id, block);
      }
    });
    this.blocks = newMap;
  }

  get sortedBlockState() {
    const state: any[] = [];
    let blockState: any;
    let i = 0;
    this.blocks.forEach((blockObj) => {
      blockState = {
        id: blockObj.id,
        sortOrder: i,
        sizeX: blockObj.sizeX,
        sizeY: blockObj.sizeY,
        posX: 0,
        posY: 0,
        locked: blockObj.positionLocked, // 'locked' was the old key name.  we should start using positionLocked
        positionLocked: blockObj.positionLocked,
      };
      if (blockState.locked) {
        // the posX and posY are only required when the block is locked.  Otherwise, saving this
        // blockState will make the Flux Capacitor not work right.  The block position is properly saved
        // on the lock/unlock action.
        blockState.posX = blockObj.posX;
        blockState.posY = blockObj.posY;
      }
      state.push(blockState);
      i++;
    });
    return state;
  }

  get loadedBlockIds() {
    return this.blocks.keys();
  }

  get visibleBlockIds() {
    const blockIds: (string | number)[] = [];
    this.blockVisibility.forEach((blockVis) => {
      if (blockVis.isVisible) {
        blockIds.push(blockVis.id);
      }
    });
    return blockIds;
  }

  get totalVisible() {
    return this.visibleBlockIds.length;
  }

  get hasVisibleBlocks() {
    return this.totalVisible !== 0;
  }

  get shouldShowMap() {
    return this.mapEnabled && this.mapVisible;
  }

  setMapEnabled(enabled: boolean) {
    this.mapEnabled = enabled;
  }

  toggleMapVisibility() {
    this.mapVisible = !this.mapVisible;
  }

  setCanEdit(val: boolean) {
    this.canEdit = val;
  }
}
